import React, { FC, ReactNode, useCallback, useState } from 'react';
import { format } from 'date-fns';
import { ExaminationPatientDTOPatientType } from '@higo/api';
import { useEachMinute } from '@higo/common/src/hooks/useEachMinute';
import { calculateWaitingTime } from '@higo/common/src/utils/dateTimeUtils';
import { HigoModeLogo, isMobileQuery } from '@higo/ui';
import { FormattedMessage } from 'react-intl';
import { usePhoneContactDetails } from '@higo/common/src/features/ExaminationPage/hooks';
import { InfoBarListContainer } from './InfoBarListContainer';
import { InfoBarListItem } from './InfoBarListItem';
import { InfoBarLabeledValue } from './InfoBarLabeledValue';
import useMedia from 'react-use/lib/useMedia';
import styled from 'styled-components';

interface ExaminationPageInfoBarProps {
  examinationCreateDate?: any;
  higoMode?: ExaminationPatientDTOPatientType;
  executedBy?: any;
  phoneNumber?: string;
  chatModalSlot?: ReactNode;
  shareModalSlot?: ReactNode;
  hasOperatorData?: boolean;
  partnerName?: string;
  isVisitPage?: boolean;
}

const MobileInfoBarContainer = styled('div')`
  width: 95%;
  margin: 0.7rem 0.8rem 1.2rem 1.5rem;
  padding-top: 0.5rem;
`;

const OperatorDataMobile = styled('div')`
  display: block;
  &:after {
    content: '';
    display: block;
  }
`;

const ModalButtons = styled('div')`
  margin-left: auto;
  margin-right: 0;
  display: flex;
`;

export const ExaminationPageInfoBar: FC<ExaminationPageInfoBarProps> = ({
  examinationCreateDate,
  higoMode,
  executedBy,
  chatModalSlot,
  shareModalSlot,
  hasOperatorData,
  partnerName,
  isVisitPage = false,
}) => {
  const formattedExamDate = format(examinationCreateDate, 'HH:mm, dd/MM/yyyy');
  const isMobileView = useMedia(`${isMobileQuery}`);

  const getWaitingTime = useCallback(
    () => examinationCreateDate && calculateWaitingTime(examinationCreateDate),
    [examinationCreateDate],
  );
  const [waiting, setWaiting] = useState(getWaitingTime);
  const updateWaitingTime = useCallback(
    () => setWaiting(getWaitingTime),
    [getWaitingTime],
  );
  useEachMinute(updateWaitingTime);

  const phoneContactDetails = usePhoneContactDetails(executedBy);
  const formattedPhoneNumber =
    phoneContactDetails &&
    phoneContactDetails?.value?.match(/.{1,3}/g)?.join(' ');

  const cutFormattedPhoneNumber =
    formattedPhoneNumber && formattedPhoneNumber?.length > 18
      ? `${formattedPhoneNumber?.slice(0, 18)}...`
      : formattedPhoneNumber;

  const cuntCuttingMOnameLength = (firtsName: string, lastName: string) => {
    const MOfullName = lastName + ' ' + firtsName;
    const lengthOfMOname = MOfullName.length;

    return lengthOfMOname > 13 ? `${MOfullName.slice(0, 13)}...` : MOfullName;
  };

  return !isMobileView ? (
    <div>
      <InfoBarListContainer>
        <InfoBarListItem>
          <div className="higo-mode">
            <HigoModeLogo mode={higoMode ?? 'FAMILY_AND_PRO'} />
          </div>
        </InfoBarListItem>
        {!isVisitPage && (
          <InfoBarListItem>
            <InfoBarLabeledValue
              id="waiting-time"
              label={
                <FormattedMessage id="examinationInfoBar.label.waitingTime" />
              }
            >
              {waiting}
            </InfoBarLabeledValue>
          </InfoBarListItem>
        )}
        <InfoBarListItem>
          <InfoBarLabeledValue
            id="exam-date"
            label={<FormattedMessage id="common.examData" />}
          >
            {formattedExamDate};{' '}
            {partnerName && partnerName?.length > 13
              ? `${partnerName?.slice(0, 13)}...`
              : partnerName}
          </InfoBarLabeledValue>
          {shareModalSlot}
        </InfoBarListItem>
        {hasOperatorData && (
          <InfoBarListItem>
            <InfoBarLabeledValue
              id="medical-operator"
              label={
                <FormattedMessage id="examinationInfoBar.label.medicalOperator" />
              }
            >
              {cuntCuttingMOnameLength(
                executedBy.firstName,
                executedBy.lastName,
              )}
            </InfoBarLabeledValue>
          </InfoBarListItem>
        )}
        {hasOperatorData && (
          <InfoBarListItem>
            <InfoBarLabeledValue
              id="contact"
              label={<FormattedMessage id="examinationInfoBar.label.contact" />}
            >
              {phoneContactDetails?.value
                ? `${
                    phoneContactDetails.prefix ?? ''
                  } ${cutFormattedPhoneNumber}`
                : '-'}
            </InfoBarLabeledValue>
            {chatModalSlot}
          </InfoBarListItem>
        )}
      </InfoBarListContainer>
    </div>
  ) : (
    <MobileInfoBarContainer>
      <>
        <InfoBarListItem>
          <HigoModeLogo
            mode={higoMode ?? 'FAMILY_AND_PRO'}
            style={{ fontSize: '15px' }}
          />

          <ModalButtons>
            {shareModalSlot}
            {chatModalSlot}
          </ModalButtons>
        </InfoBarListItem>

        <InfoBarListItem>
          <InfoBarLabeledValue
            id="exam-date"
            label={<FormattedMessage id="common.examData" />}
          >
            {formattedExamDate};{' '}
            {partnerName && partnerName?.length > 13
              ? `${partnerName?.slice(0, 13)}...`
              : partnerName}
          </InfoBarLabeledValue>
        </InfoBarListItem>

        {hasOperatorData && (
          <OperatorDataMobile>
            <InfoBarListItem>
              <InfoBarLabeledValue
                id="medical-operator"
                label={
                  <FormattedMessage id="examinationInfoBar.label.medicalOperator" />
                }
              >
                {cuntCuttingMOnameLength(
                  executedBy.firstName,
                  executedBy.lastName,
                )}
              </InfoBarLabeledValue>
              (
              {phoneContactDetails?.value
                ? `${
                    phoneContactDetails.prefix ?? ''
                  } ${cutFormattedPhoneNumber}`
                : '-'}
              )
            </InfoBarListItem>
          </OperatorDataMobile>
        )}
      </>
    </MobileInfoBarContainer>
  );
};
